import {Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faSparkles, faSpinner} from '@fortawesome/pro-solid-svg-icons';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {WorkflowService} from '../../../../services/workflow.service';

/**
 * Aktion für die Label-Zeile eines optimierbaren Textfelds (`x-optimizable`): lässt den aktuellen
 * Text von der KI verbessern und gibt den neuen Text über `valueChange` zurück.
 *
 * Nutzt denselben Endpunkt wie der KI-Editor (`WorkflowService.optimizePrompt`) und bietet danach
 * „Wiederherstellen" an, damit eine ungewollte Optimierung nicht den Originaltext kostet.
 */
@Component({
  selector: 'sf-prompt-optimize',
  standalone: true,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <!-- Optik identisch zur Prompt-Aktion im KI-Editor (ki-editor.component.html, Systemprompt/
         Prüfprompt): grüner Akzent-Link mit Sparkles-Icon, „Wiederherstellen" in Grau. -->
    @if (previousValue === null) {
      <button type="button"
              (click)="optimize()"
              [disabled]="optimizing || !text.trim()"
              class="flex items-center gap-1 text-xs text-green hover:underline
                     disabled:opacity-50 disabled:cursor-not-allowed">
        <fa-icon [icon]="optimizing ? faSpinner : faSparkles" [class.animate-spin]="optimizing"
                 class="text-xs"></fa-icon>
        {{ optimizing ? 'Verbessere…' : label }}
      </button>
    } @else {
      <button type="button" (click)="restore()"
              class="text-xs text-gray-500 hover:underline">
        Wiederherstellen
      </button>
    }
  `,
})
export class SfPromptOptimizeComponent {
  @Input() value: any;
  @Input() label = 'Prompt verbessern';
  @Output() valueChange = new EventEmitter<any>();

  optimizing = false;
  /** Text vor der Optimierung; `null` heißt „nichts zurückzunehmen". */
  previousValue: string | null = null;

  protected readonly faSparkles = faSparkles;
  protected readonly faSpinner = faSpinner;

  private readonly workflows = inject(WorkflowService);
  private readonly toast = inject(BicToastService);

  get text(): string {
    return (this.value ?? '').toString();
  }

  optimize(): void {
    const prompt = this.text.trim();
    if (!prompt || this.optimizing) return;

    this.optimizing = true;
    this.workflows.optimizePrompt(prompt).subscribe({
      next: optimized => {
        this.previousValue = this.text;
        this.optimizing = false;
        this.valueChange.emit(optimized);
      },
      error: () => {
        this.optimizing = false;
        this.toast.error('Prompt konnte nicht verbessert werden');
      },
    });
  }

  restore(): void {
    if (this.previousValue === null) return;
    const restored = this.previousValue;
    this.previousValue = null;
    this.valueChange.emit(restored);
  }
}
